import * as React from "react";
import { Controller, useFormContext } from "react-hook-form";

import {
  Checkbox,
  Field,
  FormSection,
  TextInput,
} from "@/shared/foundation/primitives";

import {
  TRIGGER_NAME_MAX_LENGTH,
  type TriggerFormValues,
} from "../triggerFormSchema";

import styles from "./sections.module.css";

export interface NameSectionProps {
  mode: "create" | "edit";
}

/**
 * What the trigger is called, and whether it starts enabled. Last, because it
 * is the one question that is easier to answer once the rest is written down.
 */
export function NameSection({ mode }: NameSectionProps) {
  const { control } = useFormContext<TriggerFormValues>();
  const titleId = React.useId();

  return (
    <FormSection
      title="Name"
      titleId={titleId}
      hint={mode === "edit" ? "A trigger can be renamed later." : undefined}
    >
      <Controller
        control={control}
        name="name"
        render={({ field, fieldState }) => (
          <Field.Root
            name={field.name}
            invalid={fieldState.invalid}
            data-testid="trigger-name-field"
          >
            {/* The section's title is the label, so it is not said twice. */}
            <Field.Control
              aria-labelledby={titleId}
              render={
                <TextInput autoComplete="off" spellCheck={false} size="lg" />
              }
              maxLength={TRIGGER_NAME_MAX_LENGTH}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
              placeholder="ingest-new-drives"
            />
            <Field.Description>
              Letters, numbers, hyphens and underscores, up to{" "}
              {TRIGGER_NAME_MAX_LENGTH} characters.
            </Field.Description>
            <Field.Error match>{fieldState.error?.message}</Field.Error>
          </Field.Root>
        )}
      />

      <Controller
        control={control}
        name="enabled"
        render={({ field }) => (
          <div className={styles.row} data-testid="trigger-enabled-field">
            <Checkbox
              checked={field.value}
              onCheckedChange={field.onChange}
              name={field.name}
              size="md"
              label={mode === "edit" ? "Enabled" : "Enable on create"}
              description="A disabled trigger is saved but never fires until it is turned on."
            />
          </div>
        )}
      />
    </FormSection>
  );
}
